import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Droplets,
  Wrench,
  Shield,
  Calendar,
  ArrowRight,
  Star,
  Quote } from
'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const services = [
{
  icon: Droplets,
  title: 'Irrigation Installation',
  description: 'Custom-designed sprinkler systems built for long-term performance and efficiency.'
},
{
  icon: Wrench,
  title: 'Maintenance Programs',
  description: 'Structured maintenance plans designed to prevent mid-season issues and extend the life of your irrigation system.'
},
{
  icon: Shield,
  title: 'Back Flow Testing',
  description: 'Certified testing services to ensure your irrigation system remains compliant and your water supply stays protected.'
}];


const testimonials = [
{
  name: 'Josh Ivy',
  location: 'Lead Manager, Lawn Works Inc.',
  text: 'Mist Irrigation has provided our company and it\'s customers with top quality irrigation systems & services for years. There is no one else to call for your irrigation needs.',
  rating: 5
},
{
  name: 'Irriland Corp',
  location: 'Irrigation, Lighting, Fountains',
  text: 'Customer Service and Craftsmanship is second to none. Highly Recommend.',
  rating: 5
},
{
  name: 'Laurie L',
  location: 'North Park, Pittsburgh, PA',
  text: 'Mist Irrigation replaced our old and outdated system with a new and more efficient layout.',
  rating: 5
}];


const Home = () => {
  return (
    <div className="pt-24 md:pt-28" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1770664945615-52203ab54c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxzcHJpbmtsZXIlMjBpcnJpZ2F0aW9uJTIwc3lzdGVtJTIwd2F0ZXJpbmclMjBncmVlbiUyMGxhd258ZW58MHx8fHwxNzcxOTQ2NDMzfDA&ixlib=rb-4.1.0&q=85"
            alt="Irrigation system watering a lush green lawn"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl">

            <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-6">
              Professional Irrigation Solutions
            </span>
            <h1 className="font-heading !font-bold !text-4xl mb-6 text-mist-white">Professional Irrigation Installation &

              <span className="text-mist-light"> Seasonal Care Programs</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-mist-white/90 leading-relaxed mb-8 max-w-2xl">Expert irrigation installation, maintenance and seasonal system care.


            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-landscape-green hover:bg-landscape-dark text-white rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="hero-cta-primary">

                  Get Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-mist-white text-mist-white hover:bg-mist-white hover:text-mist-dark rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="hero-cta-secondary">

                  Our Services
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-mist-light text-mist-blue font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
              What We Do
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-dark mb-6">Professional Irrigation Solutions

            </h2>
            <p className="font-body text-lg text-mist-blue max-w-2xl mx-auto">At Mist Irrigation, we specialize in  professional irrigation installation, maintenance and system care. Our services are designed to keep your property healthy, efficient, and protected year-round.


            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            {services.map((service, index) =>
            <motion.div key={index} variants={staggerItem} className="h-full">
                <Card className="group bg-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 rounded-2xl overflow-hidden card-hover h-full" data-testid={`service-card-${index}`}>
                  <CardContent className="p-8 h-full flex flex-col">
                    <div className="w-14 h-14 bg-mist-light rounded-2xl flex items-center justify-center mb-6 group-hover:bg-landscape-green transition-colors duration-300">
                      <service.icon className="w-7 h-7 text-mist-blue group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-mist-dark mb-3">
                      {service.title}
                    </h3>
                    <p className="font-body text-mist-blue leading-relaxed flex-grow">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>

          <motion.div {...fadeUp} className="text-center mt-12">
            <Link to="/services">
              <Button
                size="lg"
                className="bg-mist-blue hover:bg-mist-dark text-white rounded-full px-8"
                data-testid="view-all-services-btn">

                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-mist-dark" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
                Why Mist Irrigation
              </span>
              <h2 className="font-heading !font-semibold !text-3xl mb-6 text-mist-white">Irrigation Systems Designed to Perform Season After Season

              </h2>
              <p className="font-body text-lg text-mist-light/80 leading-relaxed mb-8">With years of experience in professional irrigation installation and maintenance, we focus exclusively on delivering efficient, long-lasting system performance using premium components and proven techniques. 


              </p>
              <ul className="space-y-4">
                {[
                'Licensed and insured professionals',
                'Premium quality parts and materials',
                'Custom designs for every property',
                'Comprehensive warranty coverage',
                'Structured Seasonal Care Programs'].
                map((item, index) =>
                <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-landscape-green rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-body text-mist-light">{item}</span>
                  </li>
                )}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative">

              <img
                src="https://customer-assets.emergentagent.com/job_yard-care-expert/artifacts/njfzkkgj_sprinklerimg1.jpg"
                alt="Professional irrigation installation"
                className="rounded-3xl shadow-2xl w-full h-auto object-cover" />

              <div className="absolute -bottom-8 -left-8 bg-landscape-green text-white p-6 rounded-2xl shadow-xl">
                <div className="text-4xl font-heading font-bold">30+</div>
                <div className="font-body text-sm">Years Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-mist-light text-mist-blue font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
              Testimonials
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-dark mb-6">
              What Our Customers Say
            </h2>
            <p className="font-body text-lg text-mist-blue max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our satisfied customers have to say about our services.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8">

            {testimonials.map((testimonial, index) =>
            <motion.div key={index} variants={staggerItem} className="h-full">
                <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden h-full" data-testid={`testimonial-card-${index}`}>
                  <CardContent className="p-8 h-full flex flex-col">
                    <Quote className="w-10 h-10 text-landscape-green/30 mb-4" />
                    <p className="font-body text-mist-dark leading-relaxed mb-6 flex-grow">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) =>
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    )}
                    </div>
                    <div>
                      <div className="font-heading text-lg font-bold text-mist-dark">
                        {testimonial.name}
                      </div>
                      {testimonial.location &&
                    <div className="font-body text-sm text-mist-blue">
                          {testimonial.location}
                        </div>
                    }
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-landscape-green" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">Ready for Reliable Irrigation System care?

            </h2>
            <p className="font-body text-lg text-white/90 max-w-2xl mx-auto mb-8">Contact us today for a free irrigation consultation and estimate. Let's build a system that performs season after season.

            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-landscape-green hover:bg-mist-white rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="cta-contact-btn">

                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:5551234567">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-landscape-green rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="cta-call-btn">Call (724) 294-5999


                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Seasonal Maintenance Banner */}
      <section className="py-16 bg-mist-light" data-testid="seasonal-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-mist-blue rounded-2xl flex items-center justify-center">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-mist-dark">Irrigation Maintenance Programs

                </h3>
                <p className="font-body text-mist-blue">
                  Keep your irrigation system running perfectly year-round
                </p>
              </div>
            </div>
            <Link to="/services">
              <Button
                className="bg-mist-dark hover:bg-mist-blue text-white rounded-full px-6"
                data-testid="seasonal-learn-more">

                Learn More
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;